import React, {useEffect} from 'react';
import {Formik, FormikHelpers} from 'formik';
import FormikTextField from 'src/components/FormikTextField';
import Label from 'shared/components/label';
import Yup from 'src/utils/yup';
import {AccountSettingsFormContainer} from 'shared/account-settings/FormContainer';
import {setNavigationAnnouncement} from 'shared/actions/accessibility-announcement';
import {connect, useDispatch} from 'react-redux';
import {Store} from 'src/types/store';
import {changesSaved} from 'shared/actions/notifications';
import AreYouSureModal from 'shared/account-settings/components/AreYouSureModal';
import VerifyPhoneModal from 'src/components/AccountSettings/verify-phone-modal';
import {EducationPhase} from 'src/types/enums';
import {User} from 'src/types/user';
import {Student} from 'src/types/student';
import placeholders from 'src/static/placeholders.json';
import {openModal} from 'shared/actions/modal';
import {receivePhoneNumber} from 'src/redux/verify-phone-number/actions';

interface Props {
  setNavigationAnnouncement: (announcement: string) => void;
  onSubmit: (formData: User | Student) => Promise<void>;
  initialValues: Student | User;
  userType: string;
  hideParentEmail: boolean;
}

export interface ContactInfo {
  email: string;
  parentEmail?: string;
  mobile?: string | null;
  _id: string;
  currentPassword: string;
  userType: string;
}

const contactFormSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address.'),
  parentEmail: Yup.string().email('Invalid email address.').nullable(),
  mobile: Yup.string().phoneNumber('Enter a valid phone number.').nullable(),
});

function ContactInfoForm(props: Props) {
  const dispatch = useDispatch();
  useEffect(() => {
    props.setNavigationAnnouncement('Contact Information');
  }, []);

  const initialValues: ContactInfo = {
    ...props.initialValues,
    currentPassword: '',
    userType: props.userType,
  };

  const handleSubmit = async (
    values: ContactInfo,
    formikHelpers: FormikHelpers<ContactInfo>
  ): Promise<void> => {
    try {
      await props.onSubmit(values);
      dispatch(receivePhoneNumber(values.mobile as string));
      if (values.mobile && props.initialValues.mobile !== values.mobile) {
        dispatch(openModal('VERIFY_PHONE_MODAL'));
      }
    } catch (contactInfoErrors: $TSFixMe) {
      console.error('Error updating contact info:', contactInfoErrors);
      formikHelpers.setErrors(contactInfoErrors);
    }
  };

  return (
    <>
      <VerifyPhoneModal />
      <AreYouSureModal />
      <Formik<ContactInfo>
        initialValues={initialValues}
        validationSchema={contactFormSchema}
        onSubmit={async (values, formikHelpers) => {
          await handleSubmit(values, formikHelpers);

          dispatch(receivePhoneNumber(values.mobile as string));
          if (values.mobile && props.initialValues.mobile !== values.mobile) {
            dispatch(openModal('VERIFY_PHONE_MODAL'));
          }
        }}
        validateOnBlur
      >
        {({values}) => {
          const disabled = values.currentPassword === '';

          return (
            <AccountSettingsFormContainer title="Email & Mobile">
              <Label required>
                Current Password
                <FormikTextField name="currentPassword" placeholder="Password" type="password" />
              </Label>
              <Label required>
                Email
                <FormikTextField
                  name="email"
                  placeholder="Email"
                  type="email"
                  disabled={disabled}
                />
              </Label>
              {props.userType === 'Student' && props.hideParentEmail === false && (
                <Label>
                  Parents Email
                  <FormikTextField
                    name="parentEmail"
                    placeholder="Parents Email"
                    type="email"
                    disabled={disabled}
                  />
                </Label>
              )}
              <Label>
                Mobile Number
                <FormikTextField
                  name="mobile"
                  placeholder={placeholders.tel}
                  type="phone"
                  disabled={disabled}
                />
              </Label>
              <p className="account-setting-mobile-info">
                Enter number and click &ldquo;Save Changes&rdquo; to enable text alerts. Message and
                data rates may apply. 3msg/month or more. Text HELP to (833) 334-9005 for help. Text
                STOP to (833) 334-9005 to cancel.
              </p>
            </AccountSettingsFormContainer>
          );
        }}
      </Formik>
    </>
  );
}

interface FindUserData {
  user: Student | User;
  userType: string;
}

const findUserData = (state: Store): FindUserData =>
  [
    EducationPhase.HIGH_SCHOOL,
    EducationPhase.COMMUNITY_COLLEGE,
    EducationPhase.UNDERGRADUATE,
    '',
  ].indexOf(state.userType) !== -1
    ? {user: state.student, userType: 'Student'}
    : {user: state.user, userType: state.user.type || ''};

export default connect(
  (state: Store) => {
    const {user, userType} = findUserData(state);

    return {
      user,
      userType,
      loading: state.accountSettingsLoading,
    };
  },
  {
    setNavigationAnnouncement,
    onChangeContactInfo:
      () =>
      (dispatch: $TSFixMe): void => {
        dispatch(changesSaved());
      },
  }
)(ContactInfoForm);
